import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { PlanComparisionTableComponent } from './plan-comparision-table/plan-comparision-table.component';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { PlansService } from '../../../core/services/plans.service';
import { GenericInformation } from '../../../core/models/generic-information.interface';

// Tipos para los precios de planes
type PlanFrequency = 'anual' | 'trimestral';

// Interfaz para los datos de plan recibidos del backend (extendiendo GenericInformation)
interface PlanData extends GenericInformation {
  details?: string[];
  onDemand?: string[];
  highlight?: string;
}

@Component({
  selector: 'app-plan',
  standalone: true,
  imports: [PlanComparisionTableComponent, CommonModule, RouterModule],
  templateUrl: './plan.component.html',
  styleUrl: './plan.component.scss'
})
export class PlanComponent implements OnChanges, OnInit {
  @Input() isVisible: boolean = true;
  @Input() subscriptionTypes: PlanData[] = [];
  @Output() closeDialogEvent = new EventEmitter<void>();
  
  selectedPlan: PlanFrequency = 'anual';
  loading = false;
  error: string | null = null;
  
  // Descuento aplicado al plan anual
  annualDiscount = 15;
  
  // Precios de planes según frecuencia
  planPrices: {[key: string]: {[key: string]: number}} = {};
  
  // Planes individuales para acceso más rápido
  freemiumPlan: PlanData | undefined;
  professionalPlan: PlanData | undefined;
  executivePlan: PlanData | undefined;
  
  constructor(
    private router: Router,
    private plansService: PlansService
  ) {}

  ngOnInit(): void {
    // Si no hay datos de suscripción proporcionados, los cargamos
    if (!this.subscriptionTypes || this.subscriptionTypes.length === 0) {
      this.loadSubscriptionTypes();
    } else {
      // Si ya tenemos datos, los inicializamos
      this.initializePlans();
      this.initializePlanPrices();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subscriptionTypes'] && changes['subscriptionTypes'].currentValue) {
      if (this.subscriptionTypes && this.subscriptionTypes.length > 0) {
        console.log('Tipos de suscripción recibidos:', this.subscriptionTypes);
        this.initializePlans();
        this.initializePlanPrices();
      }
    }
  }
  
  // Método para cargar los tipos de suscripción desde el servicio
  loadSubscriptionTypes(): void {
    this.loading = true;
    this.error = null;
    
    this.plansService.getSubscriptionTypes().subscribe({
      next: (response: any) => {
        // Comprobar si la respuesta tiene la estructura esperada
        if (response && response.data && Array.isArray(response.data)) {
          this.subscriptionTypes = response.data as PlanData[];
        } else if (response && Array.isArray(response)) {
          this.subscriptionTypes = response as PlanData[];
        } else if (response && response.statusCode === 200 && response.data) {
          this.subscriptionTypes = response.data as PlanData[];
        } else {
          console.error('Formato de respuesta no reconocido', response);
          this.error = 'Formato de datos no reconocido. Por favor, intente de nuevo más tarde.';
          this.loading = false;
          return;
        }
        
        this.initializePlans();
        this.initializePlanPrices();
        this.loading = false;
      },
      error: (err) => {
        console.error('Error al cargar los tipos de suscripción', err);
        this.error = 'No se pudieron cargar los programas. Por favor, intente de nuevo más tarde.';
        this.loading = false;
      }
    });
  }
  
  // Inicializa los planes individuales
  initializePlans(): void {
    this.freemiumPlan = this.findPlanByName('freemium');
    this.professionalPlan = this.findPlanByName('professional');
    this.executivePlan = this.findPlanByName('executive');
    
    console.log('Planes inicializados:', {
      freemium: this.freemiumPlan,
      professional: this.professionalPlan,
      executive: this.executivePlan
    });
  }
  
  // Busca un plan por su nombre
  findPlanByName(searchTerm: string): PlanData | undefined {
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    return this.subscriptionTypes.find(plan => {
      if (!plan.name) return false;
      const planName = plan.name.toLowerCase();
      
      if ((lowerSearchTerm === 'freemium' || lowerSearchTerm === 'freemiun') && (planName.includes('free') || planName.includes('freemium') || planName.includes('freemiun'))) {
        return true;
      } else if (lowerSearchTerm === 'professional' && (planName.includes('pro') || planName.includes('professional'))) {
        return true;
      } else if (lowerSearchTerm === 'executive' && (planName.includes('exe') || planName.includes('executive'))) {
        return true;
      }
      
      return false;
    });
  }
  
  // Inicializa los precios basados en los datos recibidos
  initializePlanPrices(): void {
    // Freemium es siempre gratuito
    this.planPrices['freemium'] = {
      anual: 0,
      trimestral: 0
    };
    
    // Professional
    if (this.professionalPlan) {
      const monthlyPrice = parseFloat(this.professionalPlan.price || '0');
      console.log('Professional monthly price:', monthlyPrice);
      
      this.planPrices['professional'] = {
        trimestral: monthlyPrice * 3,
        anual: monthlyPrice * 12 * (1 - this.annualDiscount / 100)
      };
    } else {
      // Valor por defecto si no hay plan
      this.planPrices['professional'] = {
        trimestral: 150,
        anual: 510
      };
    }
    
    // Executive
    if (this.executivePlan) {
      const monthlyPrice = parseFloat(this.executivePlan.price || '0');
      console.log('Executive monthly price:', monthlyPrice);
      
      this.planPrices['executive'] = {
        trimestral: monthlyPrice * 3,
        anual: monthlyPrice * 12 * (1 - this.annualDiscount / 100)
      };
    } else {
      // Valor por defecto si no hay plan
      this.planPrices['executive'] = {
        trimestral: 240,
        anual: 816
      };
    }
    
    console.log('Precios calculados:', this.planPrices);
  }
  
  // Obtiene los detalles de un plan
  getPlanDetails(planType: string): string[] {
    let plan: PlanData | undefined;
    
    switch (planType) {
      case 'freemium':
        plan = this.freemiumPlan;
        break;
      case 'professional':
        plan = this.professionalPlan;
        break;
      case 'executive':
        plan = this.executivePlan;
        break;
    }
    
    return plan?.details || [];
  }
  
  // Obtiene los servicios bajo demanda de un plan
  getPlanOnDemand(planType: string): string[] {
    let plan: PlanData | undefined;
    
    switch (planType) {
      case 'freemium':
        plan = this.freemiumPlan;
        break;
      case 'professional':
        plan = this.professionalPlan;
        break;
      case 'executive':
        plan = this.executivePlan;
        break;
    }
    
    return plan?.onDemand || [];
  }
  
  // Verifica si un plan tiene la etiqueta de destacado
  isPlanHighlighted(planType: string): boolean {
    let plan: PlanData | undefined;
    
    switch (planType) {
      case 'freemium':
        plan = this.freemiumPlan;
        break;
      case 'professional':
        plan = this.professionalPlan;
        break;
      case 'executive':
        plan = this.executivePlan;
        break;
    }
    
    const hasHighlight = !!plan?.highlight;
    return hasHighlight;
  }
  
  // Obtiene el texto de destacado para un plan
  getHighlightText(planType: string): string {
    let plan: PlanData | undefined;
    
    switch (planType) {
      case 'freemium':
        plan = this.freemiumPlan;
        break;
      case 'professional':
        plan = this.professionalPlan;
        break;
      case 'executive':
        plan = this.executivePlan;
        break;
    }
    
    return plan?.highlight || 'Recomendado';
  }
  
  // Obtiene el nombre del plan anterior para mostrar en "Todo lo de X además de:"
  getPreviousPlanName(planType: string): string {
    if (planType === 'professional' && this.freemiumPlan) {
      return this.freemiumPlan.name || 'Freemium';
    } else if (planType === 'executive' && this.professionalPlan) {
      return this.professionalPlan.name || 'Professional';
    }
    return '';
  }
  
  togglePlan(plan: PlanFrequency) {
    this.selectedPlan = plan;
  }

  closeDialog() {
    this.isVisible = false;
    this.closeDialogEvent.emit();
  }
  
  // Navega a la vista completa de planes
  navigateToPlansView() {
    this.closeDialog();
    this.router.navigate(['/dashboard/plans']);
  }
  
  // Método para manejar la suscripción a un plan
  subscribeToPlan(planType: string) {
    if (planType === 'freemium') {
      // Para el plan gratuito, simplemente cerramos el diálogo
      this.closeDialog();
      return;
    }
    
    // Verificamos que el plan exista en nuestro objeto de precios
    if (!this.planPrices[planType]) {
      console.error(`Plan "${planType}" no encontrado en la lista de precios`);
      this.closeDialog();
      return;
    }
    
    // Verificamos que la frecuencia seleccionada exista para este plan
    if (!this.planPrices[planType][this.selectedPlan]) {
      console.error(`Frecuencia "${this.selectedPlan}" no disponible para el plan "${planType}"`);
      this.closeDialog();
      return;
    }
    
    // Obtenemos el precio según el plan y la frecuencia seleccionados
    const planPrice = this.getTotalPrice(planType);
    let planName = planType.charAt(0).toUpperCase() + planType.slice(1);
    
    // Usar el nombre del plan desde la API si está disponible
    if (planType === 'professional' && this.professionalPlan) {
      planName = this.professionalPlan.name || planName;
    } else if (planType === 'executive' && this.executivePlan) {
      planName = this.executivePlan.name || planName;
    }
    
    // Cerramos el diálogo
    this.closeDialog();
    
    // Redirigimos a la página de pagos con los datos del plan seleccionado
    this.router.navigate(['/dashboard/payment'], {
      queryParams: {
        planId: planType,
        planName: planName,
        amount: planPrice,
        currency: 'PEN',
        frequency: this.selectedPlan
      }
    });
  }
  
  // Calcula el precio total según la frecuencia seleccionada
  getTotalPrice(planType: string): number {
    if (!this.planPrices[planType] || !this.planPrices[planType][this.selectedPlan]) {
      return 0;
    }
    
    return Math.round(this.planPrices[planType][this.selectedPlan]);
  }
  
  // Devuelve el precio para mostrar según el tipo de plan
  getDisplayPrice(planType: string): string {
    let plan: PlanData | undefined;
    
    switch (planType) {
      case 'freemium':
        plan = this.freemiumPlan;
        break;
      case 'professional':
        plan = this.professionalPlan;
        break;
      case 'executive':
        plan = this.executivePlan;
        break;
    }
    
    // Si el plan existe y tiene un precio, devolver ese precio
    if (plan && plan.price) {
      return plan.price;
    }
    
    // Valores por defecto si no hay datos
    if (planType === 'professional') {
      return '50';
    } else if (planType === 'executive') {
      return '80';
    }
    
    return '0';
  }
}